import { Box, Grid, InputAdornment, Typography } from '@mui/material'
import { colors } from 'constants/theme'
import { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** STYLE */

/** API */
import IncomeExpensesApi from 'api/finances/income-expenses.api'
import ClinicApi from 'api/master/clinic.api'
import { routeName } from 'routes/routes-name'

/** UTILS || SLICE */
import { getBranch, numberFormat } from 'utils/app.utils'
import { dateTimeToView, dateToView } from 'utils/date.utils'
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import HeaderCard from 'component/CardCustom/HeaderCard'
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker'
import TableRowCommon from 'component/Table/TableRowCommon'
import { notiError, notiSuccess } from 'component/notifications/notifications'
import moment from 'moment'

import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { selectMe } from 'app/slice/user.slice'
import TableRowExport from 'component/Pdf/TableRowExport'
import TableCustom from 'component/Table/TableCustom'
import DropdownButton from 'new-components/buttons/dropdown-button'
import MainButton from 'new-components/buttons/main-button'
import SelectInput from 'new-components/inputs/select-input'
import TextInput from 'new-components/inputs/text-input'
import AlertModal from 'new-components/modal/alert-modal'
import SnackbarComponent from 'new-components/snackbar/snackbar-component'
import _ from 'lodash'
import { useSelector } from 'react-redux'
import { exportIncomeExpensesToExcel } from './exportIncomeExpensesToExcel'
import { DropdownItem } from './style'

const permissions = {
  isView: isView(),
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate()
}

export default function IncomeExpenses() {
  const history = useHistory()
  const { t } = useTranslation()
  const user = useSelector(selectMe)
  const componentRef = useRef<any>(null)
  let docStatusBgColor = ''
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')
  const [branchId, setBranchId] = useState<number | string>(Number(getBranch()) || '')
  const [branches, setBranches] = useState<any[]>([])
  const [rangeDate, setRangeDate] = useState<string[]>([moment().startOf('month').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')])
  const [rowCount, setRowCount] = useState(0)
  const [pageLimit, setPageLimit] = useState(10)
  const [sortType, setSortType] = useState('ASC')
  const [sortBy, setSortBy] = useState('incomeExpenseId')
  const [incomeExpenses, setIncomeExpenses] = useState([])
  const [exportData, setExportData] = useState([])

  const [isOpenSnack, setIsOpenSnack] = useState(false)
  const [typeSnack, setTypeSnack] = useState<any>("success")
  const [labelSnack, setLabelSnack] = useState('')

  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [statusAlertModal, setStatusAlertModal] = useState<'DELETE' | 'PENDING_APPROVAL' | 'CANCEL'>('DELETE')
  const [id, setId] = useState<number>(0)
  const [name, setName] = useState('')

  const headCells = [
    { id: 'id', disablePadding: false, label: t('INCOME_EXPENSES.TABLE.CELL.HEADER.ID'), align: 'center', width: '80px' },
    { id: 'documentNo', disablePadding: false, label: t('INCOME_EXPENSES.TABLE.CELL.HEADER.DOCUMENT_NO'), width: '140px' },
    { id: 'branchName', disablePadding: false, label: t('INCOME_EXPENSES.TABLE.CELL.HEADER.BRANCH') },
    { id: 'documentCreationDate', disablePadding: false, label: t('INCOME_EXPENSES.TABLE.CELL.HEADER.DOCUMENT_CREATION_DATE'), align: 'center', width: '160px' },
    { id: 'expenseType', disablePadding: false, label: t('INCOME_EXPENSES.TABLE.CELL.HEADER.EXPENSE_TYPE'), width: '140px' },
    { id: 'total', disablePadding: false, align: 'right', label: t('INCOME_EXPENSES.TABLE.CELL.HEADER.TOTAL'), width: '140px' },
    { id: 'documentStatus', disablePadding: false, label: t('INCOME_EXPENSES.TABLE.CELL.HEADER.DOCUMENT_STATUS'), width: '160px' },
    { id: 'updatedBy', disablePadding: false, label: t('INCOME_EXPENSES.TABLE.CELL.HEADER.CREATED_BY'), align: 'center', width: '160px' },
    { id: 'action', disablePadding: false, label: t('INCOME_EXPENSES.TABLE.CELL.HEADER.ACTION'), align: 'center', width: '90px' }
  ]

  const loadData = async () => {
    try {
      let condition: any = {}
      if (page) condition = { ...condition, page: page }
      if (pageLimit) condition = { ...condition, pageLimit: pageLimit }
      if (search) condition = { ...condition, search: search }
      if (sortBy) condition = { ...condition, sortBy: sortBy }
      if (sortType) condition = { ...condition, sortType: sortType }
      if (status) condition = { ...condition, documentStatus: status === 'ALL' ? '' : status }
      if (branchId) condition = { ...condition, branchId: branchId }
      if (rangeDate?.[0] && rangeDate?.[1]) condition = { ...condition, dateStart: rangeDate[0], dateEnd: rangeDate[1] }

      const res = await IncomeExpensesApi.findAll(condition)
      const resExport = await IncomeExpensesApi.findAll({ ...condition, export: 1 })
      if (res.status === 200) {
        setRowCount(res.headers['x-total'])
        setIncomeExpenses(res.data)
        setExportData(resExport.data)
      }
    } catch (error) {
      notiError(t('ERROR'))
    }
  }

  const loadBranches = async () => {
    ClinicApi.findAllBranches()
      .then(({ data }) => setBranches(data))
      .catch(() => { return })
  }

  useEffect(() => {
    loadBranches()
  }, [])

  useEffect(() => {
    loadData()
  }, [page, pageLimit, sortBy, sortType])


  const documentStatus = (status: string): string => {
    const statusKeyMap: Record<string, string> = {
      PENDING_APPROVAL: 'INCOME_EXPENSES.DOCUMENT_STATUS.PENDING_APPROVAL',
      APPROVAL: 'INCOME_EXPENSES.DOCUMENT_STATUS.APPROVAL',
      UNAPPROVAL: 'INCOME_EXPENSES.DOCUMENT_STATUS.UNAPPROVAL',
      CANCEL: 'INCOME_EXPENSES.DOCUMENT_STATUS.CANCEL',
    };

    const key = statusKeyMap[status];
    return key ? t(key) : '-';
  };


  const renderData = (objData: any, no: number, exportDataValue: boolean) => {
    const num = exportDataValue === true ? no + 1 : page * pageLimit - pageLimit + no + 1
    const { incomeExpenseId, documentNo, documentDate, expenseGroup, total, documentStatus, updatedBy, updatedAt, prefix, branchName } = objData
    const documentExpenseNo = prefix + documentNo
    const branchText = branchName || _.get(_.find(branches, { branchId: objData.branchId }), 'branchName') || '-'
    let documentStatusText = ''
    if (documentStatus === 'PENDING_APPROVAL') {
      docStatusBgColor = colors.approveStatus.pending
      documentStatusText = t('INCOME_EXPENSES.DOCUMENT_STATUS.PENDING_APPROVAL')
    } else if (documentStatus === 'APPROVAL') {
      docStatusBgColor = colors.approveStatus.approve
      documentStatusText = t('INCOME_EXPENSES.DOCUMENT_STATUS.APPROVAL')
    } else if (documentStatus === 'UNAPPROVAL') {
      docStatusBgColor = colors.approveStatus.cancel
      documentStatusText = t('INCOME_EXPENSES.DOCUMENT_STATUS.UNAPPROVAL')
    } else if (documentStatus === 'CANCEL') {
      docStatusBgColor = colors.approveStatus.cancel
      documentStatusText = t('INCOME_EXPENSES.DOCUMENT_STATUS.CANCEL')
    } else {
      docStatusBgColor = 'transparent'
      documentStatusText = '-'
    }

    let expenseGroupText
    if (expenseGroup === 'INCOME') {
      expenseGroupText = t('INCOME_EXPENSES.TABLE.CELL.EXPENSE_GROUP.INCOME.TITLE')
    } else if (expenseGroup === 'EXPENSE') {
      expenseGroupText = t('INCOME_EXPENSES.TABLE.CELL.EXPENSE_GROUP.EXPENSE.TITLE')
    } else {
      return
    }


    const objRenderData = {
      key: incomeExpenseId,
      id: incomeExpenseId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: num },
        { option: 'TEXT', align: 'left', label: documentExpenseNo },
        { option: 'TEXT', align: 'left', label: branchText },
        { option: 'TEXT', align: 'left', label: dateToView(documentDate) },
        { option: 'TEXT', align: 'left', label: expenseGroupText },
        { option: 'TEXT', align: 'right', label: numberFormat(total) },
        // {
        //   option: 'COMPONENT', align: 'left', class: 'py-1', component: exportDataValue ? (<>{documentStatusText}</>) : (
        //     documentStatus !== 'PENDING_APPROVAL' ?
        //       <Box sx={{
        //         borderRadius: '4px',
        //         backgroundColor: docStatusBgColor,
        //         color: colors.white,
        //         textDecoration: 'none',
        //         height: '34px',
        //         alignContent: 'center',
        //         textAlign: 'center',
        //       }}>
        //         <span>{documentStatusText}</span></Box> :
        //       <StatusSelect
        //         onchange={(event) => {
        //           const value = event.target.value
        //           if (value) {
        //             handleChangeStatus(value, incomeExpenseId)
        //           }
        //         }}
        //         label={''}
        //         selectId="select-status"
        //         value={documentStatus}
        //         labelId="label-status"
        //         statusSelectorBgColor={docStatusBgColor}
        //         disable={false}
        //         height='34px'
        //         permissionDenied={user.hasApproveIncomeAndExpenses === 'PERMISS'}
        //       />
        //   )
        // },
        {
          option: 'COMPONENT', component: exportDataValue ? (<>{documentStatusText}</>) : (
            <Box display={'flex'} alignItems={'center'} gap={'12px'}>
              <Box sx={{ borderRadius: '50%', backgroundColor: docStatusBgColor, width: '15px', height: '15px' }} />
              <Typography> {t(`${documentStatusText}`)}</Typography>
            </Box>
          )
        },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          newIconAction: true,
          values: [
            {
              option: 'COMPONANT', label: (
                <DropdownItem disabled={documentStatus === 'CANCEL'} onClick={() => onChangeStatus(objRenderData.obj, 'CANCEL')} >
                  <span className="ml-2 align-text-bottom">{t(`BUTTON.CANCEL`)}</span>
                </DropdownItem>
              ),
            },
            {
              option: 'COMPONANT', label: (
                <DropdownItem disabled={documentStatus === 'PENDING_APPROVAL'} onClick={() => onChangeStatus(objRenderData.obj, 'PENDING_APPROVAL')} >
                  <span className="ml-2 align-text-bottom">{t(`BUTTON.RESET`)}</span>
                </DropdownItem>
              ),
            },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'VIEW', label: t(`BUTTON.VIEW`), },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: user.hasApproveIncomeAndExpenses !== 'PERMISS' ? true : permissions.isUpdate.disabled ? true : documentStatus === 'APPROVAL' ? true : false },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled ? true : documentStatus === 'APPROVAL' ? true : false }
          ]
        }
      ]
    }

    return (
      <>
        {exportDataValue === true && <TableRowExport {...objRenderData} /> ||
          <TableRowCommon {...objRenderData}
            onedit={() => onEdit(objRenderData.obj)}
            ondelete={() => handleSubmitModal(Number(objRenderData.id), 'DELETE', objRenderData.obj)}
            onView={() => onView(null, objData)}
          // rowLink={true} onClick={(e, val) => onView(e, val)} 
          />
        }
      </>
    )
  }


  const handleSubmitModal = (id: number, status: typeof statusAlertModal, obj: any) => {
    setStatusAlertModal(status)
    setId(id)
    setName(obj.prefix + obj.documentNo)
    setIsOpenAlertModal(true)
  }


  const handleConfirmModal = () => {
    setIsOpenAlertModal(false)
    if (statusAlertModal === 'DELETE') {
      onDelete()
    } else {
      IncomeExpensesApi
        .updateDocumentStatus(id, statusAlertModal)
        .then(resp => {
          if (resp.status === 200) {
            loadData()
            notiSuccess(t('INCOME_EXPENSES.MESSAGE.SUCCESS.UPDATE'))
          } else {
            notiError('Error Status: 400')
          }
        })
    }
  }


  const handleChangeRowsPerPage = (limit: number) => setPageLimit(limit)

  const handleChangeStatus = async (statusVal: string, id?: number) => {
    if (id) {
      const res = await IncomeExpensesApi.updateDocumentStatus(id, statusVal)
      if (res.status === 200) {
        loadData()
        notiSuccess(t('INCOME_EXPENSES.MESSAGE.SUCCESS.UPDATE_STATUS'))
      } else {
        notiError(t('INCOME_EXPENSES.MESSAGE.ERROR'))
      }
    } else {
      setStatus(statusVal)
    }
  }


  const handleChangePage = (val: number) => setPage(val)

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const onCreate = () => history.push(routeName.incomeExpenses + '/create')

  const onEdit = (data: any) => {
    const { incomeExpenseId } = data
    history.push(`${routeName.incomeExpenses}/update/${incomeExpenseId}`)
  }

  const onView = (e: any, data: any) => {
    const { incomeExpenseId } = data
    history.push(`${routeName.incomeExpenses}/view/${incomeExpenseId}`)
  }

  const onDelete = () => {
    if (id) {
      IncomeExpensesApi.remove(id).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('INCOME_EXPENSES.MESSAGE.SUCCESS.DELETE'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('INCOME_EXPENSES.MESSAGE.ERROR'))
        }
      })
    }
  }

  const onChangeStatus = (data: any, status: 'PENDING_APPROVAL' | 'CANCEL') => {
    handleSubmitModal(data.incomeExpenseId, status, data)
  }

  const filter = [
    { label: `${t('INPUT.SEARCH')}: ${search || '-'}` },
    { label: `${t('INCOME_EXPENSES.DOCUMENT_STATUS_LABEL')}: ${t(`INCOME_EXPENSES.DOCUMENT_STATUS.${status}`)}` },
    { label: `${t('INCOME_EXPENSES.INPUT.BRANCH')}: ${branchId ? (_.get(_.find(branches, { branchId: Number(branchId) }), 'branchName') || '-') : t('INCOME_EXPENSES.DOCUMENT_STATUS.ALL')}` },
    { label: `${t('INCOME_EXPENSES.INPUT.DATE_RANGE')}: ${rangeDate?.[0] && rangeDate?.[1] ? `${dateToView(rangeDate[0])} - ${dateToView(rangeDate[1])}` : '-'}` },
  ]

  return (
    <Box>
      <AlertModal
        isOpen={isOpenAlertModal}
        title={statusAlertModal === 'DELETE' ? t(`INCOME_EXPENSES.ALERT.TITLE_DELETE`) : t(`INCOME_EXPENSES.ALERT.CONFIRM_${statusAlertModal === 'CANCEL' ? 'CANCEL' : 'RESET'}`)}
        message={statusAlertModal === 'DELETE' ? t(`INCOME_EXPENSES.ALERT.CONFIRM_DELETE_`, { documentId: name }) : t(`INCOME_EXPENSES.ALERT.CONFIRM_${statusAlertModal === 'CANCEL' ? 'CANCEL_' : 'RESET_'}`, { documentId: name })}
        type={'error'}
        handleClose={() => setIsOpenAlertModal(false)}
        onCancel={{
          onClick: () => setIsOpenAlertModal(false),
          title: t('BUTTON.CANCEL'),
        }}
        onSubmit={{ onClick: handleConfirmModal, title: t('BUTTON.SUBMIT') }}
      />
      <SnackbarComponent
        onOpen={isOpenSnack}
        onClose={() => setIsOpenSnack(false)}
        label={labelSnack}
        type={typeSnack}
        transform={`translateX(calc(100vw - 100% - 32px)) translateY(calc(-100vh + 100% + ${window.innerHeight - 50}px))`}
        autoHideTime={3000}
      />
      <HeaderCard text={t('INCOME_EXPENSES.TITLE')} />
      <Box paddingX="24px" paddingTop="16px">
        <Grid container spacing="16px">
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <TextInput
              label={t('INCOME_EXPENSES.INPUT.MAIN_SEARCH')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
              }}
              fullWidth
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end" >
                    {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                      <FontAwesomeIcon
                        icon={faTimes}
                        onClick={() => {
                          setSearch('');
                        }}
                        style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              label={t('INCOME_EXPENSES.INPUT.BRANCH')}
              value={branchId}
              onChange={(event: any) => {
                setBranchId(event.target.value)
                setPage(1)
              }}
              options={[
                { value: '', name: t('INCOME_EXPENSES.DOCUMENT_STATUS.ALL') },
                ...branches.map((b: any) => ({ value: b.branchId, name: b.branchName }))
              ]}
              width="100%"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} xl={2}>
            <InputSecondNewRangePicker
              inputHeight={40}
              value={rangeDate || emptyRangePicker}
              onchange={(val: any) => {
                if (!_.isEmpty(val)) {
                  setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
                  setPage(1)
                }
              }}
              onClear={() => {
                setRangeDate(emptyRangePicker)
                setPage(1)
              }}
              allowClear
              label={t('INCOME_EXPENSES.INPUT.DATE_RANGE')}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              onChange={(event) => {
                const value = event.target.value
                if (value) {
                  handleChangeStatus(value)
                }
              }}
              options={[
                { value: 'ALL', name: t('INCOME_EXPENSES.DOCUMENT_STATUS.ALL') },
                { value: 'PENDING_APPROVAL', name: t('INCOME_EXPENSES.DOCUMENT_STATUS.PENDING_APPROVAL') },
                { value: 'APPROVAL', name: t('INCOME_EXPENSES.DOCUMENT_STATUS.APPROVAL') },
                { value: 'UNAPPROVAL', name: t('INCOME_EXPENSES.DOCUMENT_STATUS.UNAPPROVAL') },
                { value: 'CANCEL', name: t('INCOME_EXPENSES.DOCUMENT_STATUS.CANCEL') }
              ]}
              label={t('INCOME_EXPENSES.INPUT.STATUS_SELECTOR')}
              value={status}
              width="100%"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={1} xl={1}>
            <MainButton
              title={t('INPUT.SEARCH')}
              type='primary'
              variant='contained'
              style={{ width: '120px' }}
              onClick={() => {
                setPage(1)
                loadData()
              }} />
          </Grid>

          <Grid item xs={12} sm={12} md={12} xl={3}>
            <Box display={'flex'} justifyContent={'flex-end'} alignItems={'center'} gap={'16px'}>
              <DropdownButton title={'พิมพ์เอกสาร'} onChange={(e) => {
                  if (e === 'Excel') {
                    const report: any = {
                      title: t('INCOME_EXPENSES.TITLE'),
                      statusFilter: filter[1].label,
                      branchFilter: filter[2].label,
                      rows: exportData.map((item: any) => ({
                        documentExpenseNo: `${item?.prefix}${item?.documentNo}` || '-',
                        branchName: item?.branchName || _.get(_.find(branches, { branchId: item?.branchId }), 'branchName') || '-',
                        documentDate: dateToView(item.documentDate),
                        expenseGroupText: item?.expenseGroup === 'INCOME' ? t('INCOME_EXPENSES.TABLE.CELL.EXPENSE_GROUP.INCOME.TITLE') : t('INCOME_EXPENSES.TABLE.CELL.EXPENSE_GROUP.EXPENSE.TITLE'),
                        total: item?.total || 0,
                        status: documentStatus(item?.documentStatus),
                        updatedBy: item?.updatedBy,
                        updatedAt: dateTimeToView(item?.updatedAt)
                      })),
                      t
                    }
                    exportIncomeExpensesToExcel(report)
                  } else if (e === 'PDF') {
                    const style = document.createElement("style");
                    style.innerHTML = `
                                @media print {
                                  @page { size: landscape; }
                                }
                              `;
                    document.head.appendChild(style);
                    window.print();
                    document.head.removeChild(style);

                  }
                }} options={[{ value: 'Excel', name: ' Excel' }, { value: 'PDF', name: ' PDF' }]} />
              <MainButton title={t('INCOME_EXPENSES.BUTTON.ADD')} disabled={permissions.isCreate.disabled} type='primary' variant='contained' onClick={() => onCreate()} startIcon={<AddCircleIcon />} />
            </Box>
          </Grid>
        </Grid>

        <Box paddingTop="24px">
          <TableCustom
            page={page}
            pageLimit={pageLimit}
            sortBy={sortBy}
            sortType={sortType}
            onSort={onRequestSort}
            setPageLimit={handleChangeRowsPerPage}
            setPage={handleChangePage}
            rowCount={rowCount}
            headCells={headCells}
            rowsData={incomeExpenses.map((val, i) => {
              return renderData(val, i, false)
            })}
            tableFixedWidth
            // tableMinWidth={1280}
          />
        </Box>
      </Box>

      <Box
        id="print-table-container"
        ref={componentRef}
      >
        <Typography fontSize="14px" fontWeight={400}>{t('INCOME_EXPENSES.TITLE')}</Typography>
        <Box display='flex' flexDirection="row" alignItems="center" gap="16px">
          <Typography fontSize="10px" fontWeight={400}>{`${t('INCOME_EXPENSES.DOCUMENT_STATUS_LABEL')}: ${t(`INCOME_EXPENSES.DOCUMENT_STATUS.${status}`)}`}</Typography>
          <Typography fontSize="10px" fontWeight={400}>{filter[2].label}</Typography>
        </Box>
        <table style={{ width: '100%', border: '1px solid black', borderCollapse: 'collapse', marginTop: '16px' }}>
          <thead>
            <tr style={{ textAlign: 'center', fontSize: '9.5px', fontWeight: 700 }}>
              <th style={{ borderBottom: '1px solid black' }}>{t('#')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'center' }}>{t('INCOME_EXPENSES.TABLE.CELL.HEADER.DOCUMENT_NO')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'center' }}>{t('INCOME_EXPENSES.TABLE.CELL.HEADER.BRANCH')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'center' }}>{t('INCOME_EXPENSES.TABLE.CELL.HEADER.DOCUMENT_CREATION_DATE')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('INCOME_EXPENSES.TABLE.CELL.HEADER.EXPENSE_TYPE')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'right' }}>{t('INCOME_EXPENSES.TABLE.CELL.HEADER.TOTAL')}</th>
              <th style={{ borderBottom: '1px solid black', textAlign: 'center' }}>{t('INCOME_EXPENSES.TABLE.CELL.HEADER.DOCUMENT_STATUS')}</th>
              <th style={{ borderBottom: '1px solid black' }}>{t('INCOME_EXPENSES.TABLE.CELL.HEADER.CREATED_BY')}</th>
            </tr>
          </thead>
          <tbody>
            {exportData?.length === 0 &&
              <tr>
                <td colSpan={8} style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '100%' }}>{t('NOT_FOUND')}</td>
              </tr>
            }
            {exportData.map((data: any, index: number) => (
              <tr key={index}>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '50px' }}>{index + 1}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center' }}>{data?.prefix + data?.documentNo || '-'}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center' }}>{data?.branchName || _.get(_.find(branches, { branchId: data?.branchId }), 'branchName') || '-'}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center' }}>{dateToView(data?.documentDate)}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center' }}>{data?.expenseGroup === 'INCOME' ? t('INCOME_EXPENSES.TABLE.CELL.EXPENSE_GROUP.INCOME.TITLE') : t('INCOME_EXPENSES.TABLE.CELL.EXPENSE_GROUP.EXPENSE.TITLE')}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'right', verticalAlign: 'center' }}>{numberFormat(data?.total) || '-'}</td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '120px' }}>
                  {documentStatus(data?.documentStatus)}
                </td>
                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                  <span>{data?.updatedBy || '-'}</span>
                  <span>{dateTimeToView(data?.updatedAt) || '-'}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </Box>
  )
}
